'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCookie } from 'cookies-next';
import { useForm } from 'react-hook-form';
import { AutoCompleteAddress } from 'shared/types/account/Account';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import ToolTips from '@/components/customercenter-ui/tooltip';
import AddressLineTooltip from '@/components/customercenter-ui/tooltip/address';
import PhoneNumberTooltip from '@/components/customercenter-ui/tooltip/phoneNumber';
import { useAccount } from '@/frontastic';
import useIntuitiveSearch from '@/frontastic/hooks/useIntuitiveSearch';
import { GlobalPageTexts } from '@/helpers/i18n';
import { ErrorNotification } from '@/helpers/utils/notification';
import {
  PathNames,
  getPageUrl,
  compareAddresses,
  getSiteDivisionCode,
  getCookieValue,
  TranslateText,
  createUrl,
} from '@/helpers/utils/siteUtils';
import { CreateCustomerPayload, ValidateAddress } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { CONSTANTS_RESPONSE, COOKIE_KEYS, DEFAULT_SITE_LANG, FORM_FIELD_SPECS } from '../../../../constants/constant';
import AddCircle from '../../svgs/addCircle';
import {
  DEFAULT_COUNTRY,
  RESPONSE_POTENTIAL_MATCH_FOUND,
  RESPONSE_SUCCESS_MESSAGE,
  RESPONSE_EXISTING_CUSTOMER_FOUND,
} from '../customer-detail/constants';
import { createCustomerSchema } from '../customer-detail/schema';
import { AddressDialogModal } from '../modals/common/validateAddress';
import ConfirmCustomerModal from '../modals/confirm-customer';
import BackButton from '../ui/back-button';
import { Button } from '../ui/button';
import InputField from '../ui/customInput/input';
import InputMaskField from '../ui/customInputMask';
import { StateDropDown } from '../ui/customSelect/stateDropDown';
import Loader from '../ui/loader';
import { useToast } from '../ui/use-toast';

const CreateNewCustomer = () => {
  const { toast } = useToast();
  const router = useRouter();
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [loading, setLoading] = useState(false);
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const { createCustomer, validateAddress } = useAccount();
  const [initialStateValue, setInitialStateValue] = useState('');
  const [pickListSelectedAddress, setPickListSelectedAddress] = useState<AutoCompleteAddress>();
  const [potentialMatchFound, setPotentialMatchFound] = useState({ state: false, SuggestedData: {} });
  const [backUrl, setBackUrl] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [formattingKey, setFormattingKey] = useState('');
  const [validateAddressResponse, setValidateAddressResponse] = useState({} as any);

  const [getMailingRecordId, setGetMailingRecordId] = useState({ recordMailingIdProspect: '' });

  const handleShowadditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    reset,
    clearErrors,
    getValues,
    trigger,
    watch,
  } = useForm({
    resolver: zodResolver(createCustomerSchema),
    mode: 'all',
  });

  const firstName = watch('firstName') || '';
  const lastName = watch('lastName') || '';

  const combinedNameWarning = firstName?.length + lastName?.length >= 28 ? 'Character Limit Reached' : '';

  useIntuitiveSearch({
    setShowadditionalAddressInfo,
    setValue,
    setInitialStateValue,
    clearErrors,
    setPickListSelectedAddress,
  });

  useEffect(() => {
    const backurl = getPageUrl(PathNames.CUSTOMER_SEARCH);
    setBackUrl(backurl);
  }, []);

  const onSubmit = async (data: any) => {
    setLoading(true);

    const payload = {
      query: {
        X_IncludeSuggestedCustomers: !potentialMatchFound?.state,
        X_MailingRecordId: getMailingRecordId?.recordMailingIdProspect ?? '',
        X_INITATED_ON: '',
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
      data: {
        CustomerInfo: {
          firstName: data?.firstName,
          lastName: data?.lastName,
          companyName: data?.companyName,
        },
        BillingAddress: {
          LocationInfo: {
            line1: data?.streetName,
            line2: data?.additionalAddressInfo,
            city: data?.city,
            state: data?.state,
            zip: data?.postalCode,
            country: DEFAULT_COUNTRY,
          },
        },
        DivisionCustomers: [
          {
            divisionCode: getCookie(COOKIE_KEYS.selectedSite)?.slice(0, 2) ?? '',
            EmailRecords: [
              {
                EmailInfo: {
                  emailAddress: data?.email,
                },
              },
            ],
            PhoneRecords: [
              {
                PhoneInfo: {
                  phoneNumber: data?.phone,
                },
              },
            ],
          },
        ],
      },
    } as CreateCustomerPayload;

    await createCustomer(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const divisionCode = getCookie(COOKIE_KEYS.selectedSite);
        const CDP_URL = createUrl(
          response?.Customer?.customerId,
          response?.Customer?.CustomerInfo?.omsCustomerId,
          getSiteDivisionCode(divisionCode ?? ''),
        );
        if (
          response?.message === RESPONSE_POTENTIAL_MATCH_FOUND ||
          response?.message === RESPONSE_EXISTING_CUSTOMER_FOUND ||
          response?.SuggestedCustomers?.length > 0
        ) {
          if (
            response?.SuggestedCustomers?.[0]?.recordType === 'Prospect' &&
            response?.SuggestedCustomers?.[0]?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord
              ?.mailingRecordId
          ) {
            setGetMailingRecordId({
              recordMailingIdProspect:
                response?.SuggestedCustomers?.[0]?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord
                  ?.mailingRecordId,
            });
          } else {
            setGetMailingRecordId({
              recordMailingIdProspect: '',
            });
          }
          setPotentialMatchFound({ state: true, SuggestedData: response?.SuggestedCustomers?.[0] });
          setLoading(false);
        } else {
          setPotentialMatchFound((prev) => ({ ...prev, state: false }));
          setLoading(true);
          router.push(`/customer-details/${CDP_URL}`);
        }
      } else {
        ErrorNotification(toast, {
          description: Texts.errorWhileCreating,
          action: <ErrorIcon className="h-5 w-5" />,
        });
        setLoading(false);
      }
    });
  };
  const mergeAddressFromExactMatch = (locationInfo: any, formValues: any) => {
    formValues.streetName = locationInfo?.line1;
    formValues.additionalAddressInfo = locationInfo?.line2;
    formValues.city = locationInfo?.city;
    formValues.state = locationInfo?.state;
    formValues.postalCode = locationInfo?.zip;
    onSubmit(formValues);
  };

  const verifyAddress = async () => {
    setLoading(true);
    const formValues: any = getValues();
    const addressDiffers = compareAddresses(formValues, pickListSelectedAddress);
    const formData = {
      OriginalAddress: {
        LocationInfo: {
          line1: formValues?.streetName,
          line2: formValues?.additionalAddressInfo,
          city: formValues?.city,
          state: formValues?.state,
          zip: formValues?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    if (addressDiffers) {
      const payload = {
        data: formData,
        query: {
          formattedKey: formattingKey,
          divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
        },
      } as ValidateAddress;
      const response: any = await validateAddress(payload);

      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, formValues);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(!modalOpen);
          setValidateAddressResponse(validateAddressResponse);
        }
      } else {
        handleSubmit(onSubmit)(formValues);
      }
    } else {
      handleSubmit(onSubmit)(formValues);
    }
    setLoading(false);
  };

  const mergeDataWithFormData = async (data: any) => {
    const formData = getValues();
    const mergedData: any = { ...formData, ...data };
    const EditformData = {
      OriginalAddress: {
        LocationInfo: {
          line1: mergedData?.streetName,
          line2: mergedData?.additionalAddressInfo,
          city: mergedData?.city,
          state: mergedData?.state,
          zip: mergedData?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    const payload = {
      data: EditformData,
      query: {
        formattedKey: formattingKey,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as ValidateAddress;

    if (data?.isEditSelectedFromModel) {
      const response: any = await validateAddress(payload);

      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, mergedData);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(true);
          setValidateAddressResponse(validateAddressResponse);
          Object.keys(mergedData).forEach((fieldName: any) => {
            setValue(fieldName, mergedData[fieldName]);
          });
        }
      } else {
        onSubmit(mergedData);
      }
    } else {
      onSubmit(mergedData);
    }
  };
  return (
    <section className="p-5">
      {loading && <Loader />}
      {modalOpen && (
        <AddressDialogModal
          openModal={modalOpen}
          setOpenModal={setModalOpen}
          values={getValues()}
          validateAddressResponse={validateAddressResponse}
          mergeDataWithFormData={mergeDataWithFormData}
          setFormattingKey={setFormattingKey}
          Texts={Texts}
        />
      )}
      <ConfirmCustomerModal
        setLoading={setLoading}
        Texts={Texts}
        show={potentialMatchFound?.state}
        setShow={setPotentialMatchFound}
        suggestedData={potentialMatchFound?.SuggestedData}
        router={router}
        onSubmit={handleSubmit(onSubmit)}
        loading={loading}
      />
      <section className="flex items-center">
        <div className="mr-auto text-2xl font-bold">{Texts.createCustomer}</div>
        <BackButton text={Texts.backToCustomerSearch} className={`flex text-blueLight underline`} url={backUrl} />
      </section>

      <div className="text-xs font-normal">*{Texts.requiredFields}</div>
      <form onSubmit={handleSubmit(verifyAddress)} autoComplete="off">
        <section className="flex w-[400px] items-baseline gap-2 px-1 pt-5">
          <InputField
            items={Texts.formItemFirstName}
            id={FORM_FIELD_SPECS.firstName.id}
            htmlFor={FORM_FIELD_SPECS.firstName.id}
            register={register}
            errors={{
              ...errors,
              ...(combinedNameWarning &&
                !errors?.firstName && {
                  firstName: {
                    message: combinedNameWarning,
                  },
                }),
            }}
            label={Texts.formFirstNameLabel}
            classname="w-[400px]"
            maxLength={FORM_FIELD_SPECS.firstName.maxLength}
            focusFirstName={true}
            rules={{
              onChange: () => {
                if (getValues('lastName')) {
                  trigger(['firstName', 'lastName']);
                }
              },
            }}
            getValues={getValues}
          />
        </section>
        <section className="flex w-[400px] items-baseline gap-2 px-1 pt-5">
          <InputField
            items={Texts.formItemLastName}
            id={FORM_FIELD_SPECS.lastName.id}
            htmlFor={FORM_FIELD_SPECS.lastName.id}
            register={register}
            errors={{
              ...errors,
              ...(combinedNameWarning &&
                !errors?.lastName && {
                  lastName: {
                    message: combinedNameWarning,
                  },
                }),
            }}
            label={Texts.formLastNameLabel}
            classname="w-[400px]"
            maxLength={FORM_FIELD_SPECS.lastName.maxLength}
            rules={{
              onChange: () => {
                if (getValues('firstname')) {
                  trigger(['firstName', 'lastName']);
                }
              },
            }}
            getValues={getValues}
          />
        </section>
        {!showCompany && (
          <button type="button" className="flex items-center gap-x-2 px-1 pt-6" onClick={() => setShowCompany(true)}>
            <AddCircle className="h-5 w-5" />
            <p className="text-base font-normal leading-5">{Texts.formItemCompanyName}</p>
            <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
          </button>
        )}
        {showCompany && (
          <section className="flex w-full items-baseline px-1 pt-6">
            <section className="w-full lg:max-w-[400px]">
              <InputField
                items={Texts.formItemCompanyName}
                id={FORM_FIELD_SPECS.companyName.id}
                htmlFor={FORM_FIELD_SPECS.companyName.id}
                register={register}
                errors={errors}
                label={Texts.formCompanyNameLabel}
                classname="w-[400px]"
                maxLength={FORM_FIELD_SPECS.companyName.maxLength}
              />
            </section>
          </section>
        )}

        <section className="flex w-full items-baseline gap-x-2 px-1 pt-6">
          <section className="w-full cursor-pointer lg:max-w-[400px]">
            <InputField
              items={Texts.formItemAddress}
              id={FORM_FIELD_SPECS.addressLine1.id}
              htmlFor={FORM_FIELD_SPECS.addressLine1.id}
              register={register}
              errors={errors}
              label={FORM_FIELD_SPECS.addressLine1.id}
              classname="w-full address-input placeholder:text-white focus:placeholder:text-black"
              maxLength={FORM_FIELD_SPECS.addressLine1.maxLength}
            />
          </section>
          <ToolTips Content={{ ContentToolTip: <AddressLineTooltip /> }} />
        </section>
        {!showadditionalAddressInfo && (
          <button
            type="button"
            className="flex items-center gap-x-2 px-1 pt-6"
            onClick={handleShowadditionalAddressInfo}
          >
            <AddCircle className="h-5 w-5" />
            <p className="text-base font-normal leading-5">{Texts.formItemAdditionalAddressInfo}</p>
            <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
          </button>
        )}
        {showadditionalAddressInfo && (
          <section className={`flex w-full items-baseline px-1 pt-6 ${showadditionalAddressInfo ? 'block' : 'hidden'}`}>
            <section className="w-full lg:max-w-[400px]">
              <InputField
                items={Texts.formItemAdditionalAddressInfo}
                id={FORM_FIELD_SPECS.additionalAddressInfo.id}
                htmlFor={FORM_FIELD_SPECS.additionalAddressInfo.id}
                register={register}
                errors={errors}
                label={FORM_FIELD_SPECS.additionalAddressInfo.id}
                maxLength={FORM_FIELD_SPECS.additionalAddressInfo.maxLength}
                classname="w-full addressLine2"
              />
            </section>
          </section>
        )}
        <section className="flex w-[400px] items-baseline gap-2 px-1 pt-5">
          <InputField
            items={Texts.formItemCity}
            id={FORM_FIELD_SPECS.city.id}
            htmlFor={FORM_FIELD_SPECS.city.id}
            register={register}
            errors={errors}
            label={FORM_FIELD_SPECS.city.id}
            classname="w-full city"
            maxLength={FORM_FIELD_SPECS.city.maxLength}
          />
        </section>
        <section className="flex w-full items-baseline gap-4 px-1 pt-6">
          <section className="mb-3 w-full lg:max-w-[219px]">
            <StateDropDown
              setValue={setValue}
              errors={errors}
              label={FORM_FIELD_SPECS.state.id}
              classname="w-full state"
              initialState={initialStateValue}
              register={register}
              id={FORM_FIELD_SPECS.state.id}
            />
          </section>
          <section className="mt-2 w-full lg:max-w-[158px]">
            <InputField
              items={Texts.formItemZipCode}
              id={FORM_FIELD_SPECS.postalCode.id}
              htmlFor={FORM_FIELD_SPECS.postalCode.id}
              register={register}
              errors={errors}
              label={FORM_FIELD_SPECS.postalCode.id}
              classname="w-full zip"
              maxLength={FORM_FIELD_SPECS.postalCode.maxLength}
            />
          </section>
        </section>
        <section className="mb-2 flex w-full items-baseline gap-x-2 px-1 pt-6">
          <section className="w-full lg:max-w-[400px]">
            <InputMaskField
              items={'Phone Number'}
              id={FORM_FIELD_SPECS.phone.id}
              htmlFor={FORM_FIELD_SPECS.phone.id}
              register={register}
              errors={errors}
              label={Texts.formPhoneNumberLabel}
              classname="w-full"
              mask={'999-999-9999'}
            />
          </section>

          <ToolTips Content={{ ContentToolTip: <PhoneNumberTooltip /> }} />
        </section>
        <section className="flex w-[400px] items-baseline gap-2 px-1 pt-5">
          <InputField
            items={Texts.formItemEmailAddress}
            id={FORM_FIELD_SPECS.email.id}
            htmlFor={FORM_FIELD_SPECS.email.htmlFor}
            register={register}
            errors={errors}
            label={FORM_FIELD_SPECS.email.label}
            classname="w-[400px]"
            maxLength={FORM_FIELD_SPECS.emailAddress.maxLength}
          />
        </section>
        <section className="hidden">
          <InputField
            id={FORM_FIELD_SPECS.country.id}
            htmlFor={FORM_FIELD_SPECS.country.id}
            register={register}
            errors={errors}
            label={FORM_FIELD_SPECS.country.id}
            classname="w-full country"
            value={FORM_FIELD_SPECS.country.default}
            type={FORM_FIELD_SPECS.country.type}
          />
        </section>
        <section className="mt-4 flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" type="submit">
            {Texts.saveButton}
          </Button>
          <Button
            type="reset"
            className="p-0 underline"
            onClick={() => {
              reset({});
              setLoading(true);
              router.push(`/${PathNames.CUSTOMER_SEARCH}/`);
            }}
          >
            {Texts.cancelButton}
          </Button>
        </section>
      </form>
    </section>
  );
};

export default CreateNewCustomer;
